// src/screens/LoginScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const LoginScreen = () => {
  const { login, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const resetSuccess = searchParams.get('reset') === 'success';

  useEffect(() => {
    if (user) {
      // Check for redirect parameter. Only allow internal, same-origin paths
      // (must start with a single "/") to prevent open-redirect to external
      // sites via "//evil.com" or "https://evil.com".
      const redirectPath = searchParams.get('redirect');
      const isSafeRedirect =
        redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//');
      if (isSafeRedirect) {
        navigate(redirectPath);
        return;
      }

      // Default redirect based on role
      if (user.role === 'kiosk') {
        navigate('/kiosk');
      } else if (user.role === 'dealer_admin' || user.role === 'dealer_staff') {
        navigate('/dealer');
      } else if (user.role === 'admin') {
        navigate('/admin/dealers');
      } else {
        navigate('/vehicles');
      }
    }
  }, [user, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      // After successful login, the useEffect will handle redirect based on user role
    } catch (err) {
      console.error(err);
      // Show the server's reason (e.g. a temporary lock after failed attempts).
      alert(err.response?.data?.message && err.response.status !== 400
        ? err.response.data.message
        : 'Login failed. Check your email/password.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen lg:min-h-0 lg:py-12 lg:px-4 bg-gradient-to-br from-customer-teal-light/30 via-white to-customer-accent-light/30 dark:from-app-bg dark:via-app-bg dark:to-app-bg customer-friendly">
      <div className="w-full max-w-md mx-auto">
        {/* Welcome Header */}
        <div className="text-center py-8 lg:py-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-app-text-light dark:text-app-text mb-2">
            Welcome Back!
          </h1>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary">
            Ready to find your next adventure?
          </p>
          {resetSuccess && (
            <p className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
              Password reset successfully! Please sign in.
            </p>
          )}
        </div>
      </div>
      <form
        onSubmit={handleSubmit}
        className="flex-1 p-6 lg:p-8 space-y-5 text-sm w-full max-w-md mx-auto bg-white dark:bg-app-bg-secondary rounded-3xl shadow-xl shadow-customer-teal/10 border border-app-border-light/50 dark:border-app-border"
      >
        <div>
          <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Email</label>
          <input
            type="email"
            placeholder="your@email.com"
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-teal/50 focus:border-customer-teal transition-all"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Password</label>
            <Link to="/forgot-password" className="text-xs text-customer-accent font-medium hover:text-customer-accent-hover transition-colors">
              Forgot Password?
            </Link>
          </div>
          <input
            type="password"
            placeholder="Your password"
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-teal/50 focus:border-customer-teal transition-all"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover hover:from-customer-teal-hover hover:to-customer-teal-active text-white text-base font-bold shadow-lg shadow-customer-teal/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
        >
          Let's Go!
        </button>

        <div className="text-sm text-center text-app-text-secondary-light dark:text-app-text-secondary mt-4 pt-4 border-t border-app-border-light/50 dark:border-app-border">
          New here?{' '}
          <Link to="/register" className="text-customer-accent font-semibold hover:text-customer-accent-hover transition-colors">
            Join the fun!
          </Link>
        </div>
      </form>
    </div>
  );
};

export default LoginScreen;